/* The month, which answers a different question from the day.
 *
 * The day view answers "what am I doing now". It cannot answer "is October
 * survivable", and that is the question people open a calendar to ask — the
 * one you ask before saying yes to something in three weeks' time.
 *
 * What a cell shows is therefore not a miniature of the day. It is two facts:
 *
 *   · **What is fixed**, by name. Appointments, meetings, anything pinned —
 *     the things that will still be there whatever the planner decides. A
 *     month that lists every focus block is a wall of text you cannot read at
 *     a glance, and the focus blocks are the part that moves anyway.
 *
 *   · **How full the day is**, as one bar you can compare across a row. Full
 *     is not the same as bad; it is the shape of a week, and the shape is what
 *     you are looking for.
 *
 * The green mark is the one piece of editorialising, and it is deliberate: a
 * day with time in it that was not work. Across a month those marks are the
 * honest answer to "am I actually getting a life", and they are the same
 * minutes the rewards ledger counts.
 *
 * ACCESSIBILITY. A calendar is a grid and is built as one: `role="grid"` with
 * rows and gridcells, one tab stop for the whole month, and the arrow keys to
 * move within it — the pattern people who navigate by keyboard already know
 * from every other calendar. Each cell carries a spoken summary, because
 * "14" on its own tells a screen-reader user nothing about the day.
 */

import { InfoToggle } from '../ui/Section';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { MonthDay, MonthSummary } from '@shared/types';
import { get, errorMessage } from '../api';
import { useSession, localToday } from '../session';
import ViewSwitch from '../ui/ViewSwitch';
import Icon from '../ui/Icon';
import { useToast } from '../ui/toast';
import AddToCalendar from '../ui/AddToCalendar';
import { readContext } from '../ui/dayContext';
import { friendlyDate } from '../ui/time';

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

/* A day is "full" against this rather than against 24 hours: a bar measured
   against the whole clock makes every real day look empty, and a bar measured
   against your working hours makes an evening look like overtime. Ten hours is
   roughly a long day including the evening, and the bar is a comparison
   between days rather than a percentage of anything. */
const FULL_DAY_MIN = 10 * 60;

const monthName = (month: string) => {
  try {
    return new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' })
      .format(new Date(month + '-01T12:00:00Z'));
  } catch { return month; }
};

const shiftMonth = (month: string, by: number) => {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + by, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};

const hours = (min: number) => min >= 60
  ? `${Math.round(min / 6) / 10}h`
  : `${min}m`;

/** Monday = 0, so a week starts where the grid starts. */
const weekdayOf = (date: string) =>
  (new Date(date + 'T12:00:00Z').getUTCDay() + 6) % 7;

/** What a screen reader should hear instead of a number. */
function spoken(day: MonthDay, today: string): string {
  const when = new Intl.DateTimeFormat(undefined,
    { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' })
    .format(new Date(day.date + 'T12:00:00Z'));

  const bits: string[] = [when + (day.date === today ? ', today' : '')];
  const ctx = readContext(day.context);
  if (ctx.blocked) bits.push(ctx.reason || ctx.label || 'away');
  else if (ctx.label) bits.push(ctx.label);

  if (!day.blocks) bits.push('nothing planned');
  else {
    bits.push(`${hours(day.plannedMin)} planned`);
    if (day.fixed.length)
      bits.push(day.fixed.map(f => `${f.title} at ${f.start}`).join(', '));
    if (day.lifeMin) bits.push(`${hours(day.lifeMin)} for yourself`);
  }
  return bits.join('. ') + '.';
}

export default function MonthScreen() {
  const { user, today, loading } = useSession();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  /* The day a dialog is asking about. A month is where you notice that the
     14th is empty, so it is where putting something on the 14th belongs. */
  const [placing, setPlacing] = useState<string | null>(null);
  const realToday = today ?? localToday(user?.tz);

  const [month, setMonth] = useState(() => realToday.slice(0, 7));
  /* Which cell the arrow keys are on. One tab stop for the whole grid — a
     calendar with thirty tab stops is a calendar people tab straight past. */
  const [cursor, setCursor] = useState<string | null>(null);
  const gridRef = useRef<HTMLDivElement | null>(null);

  const summary = useQuery({
    queryKey: ['month', month],
    enabled: !loading,
    queryFn: async () => {
      const r = await get<MonthSummary>(`/api/plan/month?month=${month}`);
      if (!r.ok) throw new Error(errorMessage(r, 'Could not load that month.'));
      return r;
    }
  });

  const days = summary.data?.days ?? [];

  /* The cursor follows the month, and lands on today when today is in it —
     which is where somebody opening a calendar is looking. */
  useEffect(() => {
    if (!days.length) return;
    setCursor(prev => {
      if (prev && days.some(d => d.date === prev)) return prev;
      return days.find(d => d.date === realToday)?.date ?? days[0].date;
    });
  }, [month, days.length, realToday]);

  useEffect(() => {
    document.title = `${monthName(month)} · Amon`;
    return () => { document.title = 'Amon'; };
  }, [month]);

  /* Leading blanks so the first of the month falls on its real weekday. They
     are `aria-hidden` rather than empty gridcells: "blank" announced six times
     before Monday the first is noise, not information. */
  const lead = days.length ? weekdayOf(days[0].date) : 0;

  const totals = useMemo(() => ({
    planned: days.reduce((a, d) => a + d.plannedMin, 0),
    life: days.reduce((a, d) => a + d.lifeMin, 0),
    lifeDays: days.filter(d => d.lifeMin > 0).length
  }), [days]);

  function move(from: string, by: number) {
    const d = new Date(from + 'T12:00:00Z');
    d.setUTCDate(d.getUTCDate() + by);
    const next = d.toISOString().slice(0, 10);
    if (next.slice(0, 7) !== month) {
      /* Walking off the edge changes the month rather than stopping dead,
         which is what every calendar does and what fingers expect. */
      setMonth(next.slice(0, 7));
      setCursor(next);
      return;
    }
    setCursor(next);
    requestAnimationFrame(() => {
      gridRef.current?.querySelector<HTMLElement>(`[data-date="${next}"]`)?.focus();
    });
  }

  function onKeyDown(e: React.KeyboardEvent) {
    /* The day under the cursor is read from the cell that has focus, not from
       state. Focusing a cell sets the state through its onFocus handler, and
       React has not applied that by the time a key pressed in the same tick
       reaches here — so "click a day, press n" would open the dialog on
       yesterday's cursor. The DOM already knows the answer. */
    const focused = (e.target as HTMLElement)?.dataset?.date;
    const current = focused || cursor;
    if (!current) return;

    const by = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: 7, ArrowUp: -7 }[e.key];
    if (by !== undefined) { e.preventDefault(); move(current, by); return; }
    if (e.key === 'Home') { e.preventDefault(); move(current, -weekdayOf(current)); return; }
    if (e.key === 'End') { e.preventDefault(); move(current, 6 - weekdayOf(current)); return; }
    if (e.key === 'PageUp') { e.preventDefault(); setMonth(m => shiftMonth(m, -1)); return; }
    if (e.key === 'PageDown') { e.preventDefault(); setMonth(m => shiftMonth(m, 1)); return; }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      navigate(`/day?date=${current}`);
      return;
    }
    /* The one thing you want to do from a month without leaving it. */
    if (e.key === 'n' || e.key === 'p') { e.preventDefault(); setPlacing(current); }
  }

  return (
    <div className="screen">
      <ViewSwitch />

      <header className="topbar">
        <div className="inline">
          <button className="btn btn-ghost btn-icon" aria-label="Previous month" title="Previous month"
                  onClick={() => setMonth(m => shiftMonth(m, -1))}><Icon name="left" /></button>
          <div>
            <h1>{monthName(month)}</h1>
            <p className="muted">
              {summary.isPending
                ? 'Reading the month…'
                : `${hours(totals.planned)} planned · ${totals.lifeDays} ` +
                  `day${totals.lifeDays === 1 ? '' : 's'} with time for yourself`}
            </p>
          </div>
          <button className="btn btn-ghost btn-icon" aria-label="Next month" title="Next month"
                  onClick={() => setMonth(m => shiftMonth(m, 1))}><Icon name="right" /></button>
        </div>
        <div className="inline">
          {month !== realToday.slice(0, 7) && (
            <button className="btn btn-ghost btn-sm"
                    onClick={() => setMonth(realToday.slice(0, 7))}><Icon name="today" size={16} />This month</button>
          )}
          <button className="btn btn-ghost btn-sm"
                  onClick={() => setPlacing(cursor ?? realToday)}>
            <Icon name="plus" size={16} />Put something on a day
          </button>
          <button className="btn btn-primary btn-sm"
                  onClick={() => navigate('/day')}><Icon name="calendar" size={16} />Open today</button>
        </div>
      </header>

      {summary.isError && (
        <div className="notice notice-bad" role="alert">
          <p><strong>Could not load that month.</strong></p>
          <p className="muted small">{(summary.error as Error).message}</p>
          <button className="btn btn-ghost btn-sm" onClick={() => summary.refetch()}>Try again</button>
        </div>
      )}

      {/* How to read and drive the grid, one tap away rather than a line of
          grey text above it on every visit. */}
      <div className="month-hint">
        <span className="legend"><i className="legend-dot legend-life" />time for yourself</span>
        <span className="legend"><i className="legend-bar" />how full the day is</span>
        <InfoToggle about="the month"
                    text={<>Arrow keys move a day, <kbd>Enter</kbd> opens it, <kbd>n</kbd> puts
                      something on it. A green mark is a day with time in it that was not work.</>} />
      </div>

      <div className="month" role="grid" aria-label={`${monthName(month)}, by week`}
           ref={gridRef} onKeyDown={onKeyDown}>
        <div className="month-head" role="row">
          {WEEKDAYS.map(d => (
            <div key={d} role="columnheader" className="month-dow">
              {/* The long name is what is announced; the short one is what is
                  drawn. Two spellings of Wednesday, one of them for people
                  who cannot see the column. */}
              <abbr title={d}>{d.slice(0, 3)}</abbr>
            </div>
          ))}
        </div>

        {chunk<MonthDay | null>([...Array<MonthDay | null>(lead).fill(null), ...days], 7).map((week, wi) => (
          <div className="month-week" role="row" key={wi}>
            {week.map((day, di) => day === null ? (
              <div key={`blank-${di}`} className="month-cell is-blank" aria-hidden="true" />
            ) : (
              <Cell key={day.date} day={day} today={realToday}
                    focused={cursor === day.date}
                    onOpen={() => navigate(`/day?date=${day.date}`)}
                    onFocus={() => setCursor(day.date)} />
            ))}
          </div>
        ))}
      </div>

      {placing && (
        <AddToCalendar
          date={placing}
          onClose={() => setPlacing(null)}
          onAdded={(what, repeat) => {
            void qc.invalidateQueries({ queryKey: ['month'] });
            void qc.invalidateQueries({ queryKey: ['plan'] });
            void qc.invalidateQueries({ queryKey: ['year'] });
            void qc.invalidateQueries({ queryKey: ['rituals'] });
            toast.say(
              repeat === 'weekly' ? `“${what}” is held every week now.`
                : repeat === 'yearly' ? `“${what}” comes round every year now.`
                : `“${what}” is on ${friendlyDate(placing)}.`, 'good');
          }} />
      )}

      {summary.data && totals.life === 0 && (
        <p className="muted small">
          Nothing this month is filed as anything but work. Add a hobby on the Hobbies
          screen and the time you give it shows up here as a green mark.
        </p>
      )}
    </div>
  );
}

function Cell({ day, today, focused, onOpen, onFocus }: {
  day: MonthDay;
  today: string;
  focused: boolean;
  onOpen: () => void;
  onFocus: () => void;
}) {
  const isToday = day.date === today;
  const past = day.date < today;
  const load = Math.min(100, Math.round((day.plannedMin / FULL_DAY_MIN) * 100));
  const number = Number(day.date.slice(-2));

  return (
    <div
      role="gridcell"
      data-date={day.date}
      /* One tab stop for the grid: the cell under the cursor is the only one
         in the tab order, and the arrow keys move it. */
      tabIndex={focused ? 0 : -1}
      aria-current={isToday ? 'date' : undefined}
      aria-label={spoken(day, today)}
      className={`month-cell${isToday ? ' is-today' : ''}${past ? ' is-past' : ''}` +
        `${readContext(day.context).blocked ? ' is-blocked' : ''}`}
      onFocus={onFocus}
      onClick={() => { onFocus(); onOpen(); }}
      onDoubleClick={onOpen}>

      <div className="month-cell-head">
        <span className="month-num">{number}</span>
        {/* Not a badge and not a count: one mark, meaning the day had
            something in it that was yours. */}
        {day.lifeMin > 0 && <span className="month-life" title={`${hours(day.lifeMin)} for yourself`} />}
      </div>

      {readContext(day.context).label && (
        <span className="month-context">{readContext(day.context).label}</span>
      )}

      <ul className="month-items">
        {day.fixed.slice(0, 3).map((f, i) => (
          <li key={i} className={f.appointment ? 'is-appt' : ''}>
            <em>{f.start}</em> {f.title}
          </li>
        ))}
        {day.fixed.length > 3 && (
          <li className="muted">+{day.fixed.length - 3} more</li>
        )}
      </ul>

      {day.plannedMin > 0 && (
        <div className="month-load" aria-hidden="true">
          <i style={{ width: `${load}%` }} />
        </div>
      )}
    </div>
  );
}

function chunk<T>(list: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size));
  /* The last row is padded so the grid stays rectangular — a ragged final week
     makes every cell in it a different width. */
  const tail = out[out.length - 1];
  if (tail && tail.length < size) out[out.length - 1] = [...tail, ...Array(size - tail.length).fill(null)];
  return out;
}
